/**
 * Materiais por especialista de IA — Fase 4 do roadmap de IA (`db/migrations/V0021`).
 *
 * Sem shot/wireframe: a tela não existia quando os handoffs foram desenhados (ver
 * `navigation.ts`). O Runtime injeta o conteúdo de cada material no prompt do
 * especialista correspondente ANTES de responder — é configuração GLOBAL, sem
 * vínculo a cliente (mesmo molde dos catálogos de `ConfiguracoesGlobais.tsx`).
 *
 * 🔒 `especialista`/`formato`/`chave` nunca mudam depois de criado (o backend recusa
 * aceitar os três campos no PATCH) — por isso o formulário de edição só tem
 * `titulo`/`ativo`/`conteudo`, nunca um jeito de "mudar de especialista".
 */
import { useState } from 'react'

import type { EspecialistaIa, FormatoDoMaterial, Material } from '@/api/contracts/materiais'
import {
  obterUrlDeDownloadDeMaterial,
  useAtualizarMaterial,
  useCriarMaterial,
  useExcluirMaterial,
  useMateriais,
} from '@/api/queries/materiais'
import { tipoDoArquivo } from '@/api/upload'
import { mensagemDoErro } from '@/components/erros'
import { EsqueletoDeLista } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { AreaDeTexto, Botao, Campo, Entrada, Switch } from '@/components/primitivos'
import { ZonaDeArquivo } from '@/components/ZonaDeArquivo'
import { formatarTamanhoDeArquivo } from '@/format'
import { usePermission } from '@/permissions/usePermission'

const ESPECIALISTAS: { valor: EspecialistaIa; rotulo: string }[] = [
  { valor: 'finops', rotulo: 'FinOps' },
  { valor: 'orcamento', rotulo: 'Orçamento' },
  { valor: 'compliance', rotulo: 'Compliance' },
]

const ICONE_DO_FORMATO: Record<FormatoDoMaterial, string> = {
  pdf: 'file-pdf',
  docx: 'file-doc',
  texto: 'note',
}

export function MateriaisEspecialista() {
  const { editar } = usePermission('admin.materiais_ia')
  const [especialista, setEspecialista] = useState<EspecialistaIa>('finops')
  const consulta = useMateriais(especialista)
  const [criando, setCriando] = useState(false)

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="type-h4 text-text-primary">Materiais por especialista de IA</h2>
        {editar && !criando && (
          <Botao variante="secundaria" icone="plus" onClick={() => setCriando(true)}>
            novo material
          </Botao>
        )}
      </div>

      <div className="flex gap-2" role="group" aria-label="Especialista">
        {ESPECIALISTAS.map((e) => (
          <Botao
            key={e.valor}
            variante={especialista === e.valor ? 'accent' : 'secundaria'}
            aria-pressed={especialista === e.valor}
            onClick={() => {
              setEspecialista(e.valor)
              setCriando(false)
            }}
          >
            {e.rotulo}
          </Botao>
        ))}
      </div>

      {criando && editar && (
        <div className="bg-subtle rounded-card p-5">
          <NovoMaterial especialista={especialista} aoFechar={() => setCriando(false)} />
        </div>
      )}

      {consulta.isPending ? (
        <EsqueletoDeLista rotulo="Carregando materiais…" itens={3} />
      ) : consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : consulta.data.length === 0 ? (
        <div className="border-border-subtle bg-surface-card rounded-card border">
          <EstadoVazio
            titulo="Nenhum material cadastrado para este especialista."
            descricao={editar ? 'Cadastre o primeiro pelo formulário acima.' : undefined}
          />
        </div>
      ) : (
        <ListaDeMateriais materiais={consulta.data} editar={editar} />
      )}

      <p className="rounded-card border-border-subtle bg-navy-50 text-text-body border p-4 type-sm">
        <i className="ph ph-info text-iox-navy mr-1.5" aria-hidden="true" />
        O Runtime de IA injeta o conteúdo destes materiais no prompt do especialista
        correspondente antes de responder. É configuração <strong>global</strong>, sem vínculo a
        cliente.
      </p>
    </section>
  )
}

function ListaDeMateriais({ materiais, editar }: { materiais: Material[]; editar: boolean }) {
  const [editando, setEditando] = useState<string | null>(null)

  return (
    <div className="border-border-subtle bg-surface-card rounded-card overflow-hidden border">
      {materiais.map((material) => (
        <div key={material.id} className="border-border-subtle border-b p-5 last:border-b-0">
          {editando === material.id ? (
            <EditarMaterial material={material} aoFechar={() => setEditando(null)} />
          ) : (
            <LinhaDeMaterial
              material={material}
              editar={editar}
              aoEditar={() => setEditando(material.id)}
            />
          )}
        </div>
      ))}
    </div>
  )
}

function LinhaDeMaterial({
  material,
  editar,
  aoEditar,
}: {
  material: Material
  editar: boolean
  aoEditar: () => void
}) {
  const atualizar = useAtualizarMaterial()
  const [baixando, setBaixando] = useState(false)
  const [erroDeDownload, setErroDeDownload] = useState<string | null>(null)

  async function baixar() {
    setErroDeDownload(null)
    setBaixando(true)
    try {
      window.open(await obterUrlDeDownloadDeMaterial(material.id), '_blank', 'noopener')
    } catch (erro) {
      setErroDeDownload(mensagemDoErro(erro))
    } finally {
      setBaixando(false)
    }
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <i
          className={`ph ph-${ICONE_DO_FORMATO[material.formato]} text-iox-navy mt-0.5 text-xl`}
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="type-label text-text-primary">{material.titulo}</span>
          {material.formato === 'texto' ? (
            <span className="type-sm text-text-muted line-clamp-2">{material.conteudo}</span>
          ) : (
            <span className="type-sm text-text-muted">
              {material.formato.toUpperCase()}
              {material.tamanhoBytes !== null && ` · ${formatarTamanhoDeArquivo(material.tamanhoBytes)}`}
            </span>
          )}
          {!material.ativo && (
            <span className="type-sm text-warning-700">inativo — não entra no prompt</span>
          )}
          {erroDeDownload && <span className="type-sm text-danger-700">{erroDeDownload}</span>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {material.formato !== 'texto' && (
          <button
            type="button"
            aria-label={`baixar ${material.titulo}`}
            disabled={baixando}
            onClick={() => void baixar()}
            className="text-icon-default hover:text-iox-teal disabled:opacity-50"
          >
            <i className="ph ph-download-simple" aria-hidden="true" />
          </button>
        )}
        {editar && (
          <>
            <Switch
              ligado={material.ativo}
              rotulo={`${material.ativo ? 'desativar' : 'ativar'} ${material.titulo}`}
              ocupado={atualizar.isPending}
              aoAlternar={(proximo) =>
                atualizar.mutate({ id: material.id, patch: { ativo: proximo } })
              }
            />
            <button
              type="button"
              aria-label={`editar ${material.titulo}`}
              onClick={aoEditar}
              className="text-icon-default hover:text-iox-teal"
            >
              <i className="ph ph-pencil-simple" aria-hidden="true" />
            </button>
            <ExcluirMaterial material={material} />
          </>
        )}
      </div>
    </div>
  )
}

function EditarMaterial({ material, aoFechar }: { material: Material; aoFechar: () => void }) {
  const atualizar = useAtualizarMaterial()
  const [titulo, setTitulo] = useState(material.titulo)
  const [conteudo, setConteudo] = useState(material.conteudo ?? '')

  const faltaTitulo = !titulo.trim()
  const faltaConteudo = material.formato === 'texto' && !conteudo.trim()

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (faltaTitulo || faltaConteudo) return
        atualizar.mutate(
          {
            id: material.id,
            patch:
              material.formato === 'texto'
                ? { titulo: titulo.trim(), conteudo: conteudo.trim() }
                : { titulo: titulo.trim() },
          },
          { onSuccess: aoFechar },
        )
      }}
    >
      <Campo rotulo="Título" obrigatorio>
        <Entrada value={titulo} onChange={(e) => setTitulo(e.target.value)} />
      </Campo>
      {material.formato === 'texto' && (
        <Campo rotulo="Conteúdo" obrigatorio>
          <AreaDeTexto rows={5} value={conteudo} onChange={(e) => setConteudo(e.target.value)} />
        </Campo>
      )}
      {atualizar.isError && (
        <p role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(atualizar.error)}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Botao variante="primaria" type="submit" disabled={atualizar.isPending}>
          {atualizar.isPending ? 'salvando…' : 'salvar'}
        </Botao>
        <Botao variante="discreta" onClick={aoFechar}>
          cancelar
        </Botao>
      </div>
    </form>
  )
}

function ExcluirMaterial({ material }: { material: Material }) {
  const excluir = useExcluirMaterial()
  const [confirmando, setConfirmando] = useState(false)

  if (confirmando) {
    return (
      <span
        role="alertdialog"
        aria-label={`Excluir ${material.titulo}`}
        className="flex flex-wrap items-center gap-2"
      >
        <span className="type-sm text-danger-700">excluir material?</span>
        <Botao
          variante="primaria"
          disabled={excluir.isPending}
          onClick={() => excluir.mutate(material.id, { onSuccess: () => setConfirmando(false) })}
        >
          excluir
        </Botao>
        <Botao variante="discreta" onClick={() => setConfirmando(false)}>
          cancelar
        </Botao>
        {excluir.isError && <span className="type-sm text-danger-700">{mensagemDoErro(excluir.error)}</span>}
      </span>
    )
  }

  return (
    <button
      type="button"
      aria-label={`excluir ${material.titulo}`}
      onClick={() => setConfirmando(true)}
      className="text-icon-default hover:text-danger-700"
    >
      <i className="ph ph-trash" aria-hidden="true" />
    </button>
  )
}

function NovoMaterial({
  especialista,
  aoFechar,
}: {
  especialista: EspecialistaIa
  aoFechar: () => void
}) {
  const criar = useCriarMaterial()
  const [formato, setFormato] = useState<'arquivo' | 'texto'>('arquivo')
  const [titulo, setTitulo] = useState('')
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [recusado, setRecusado] = useState<string | null>(null)
  const [conteudo, setConteudo] = useState('')
  const [tentou, setTentou] = useState(false)

  const faltando = [
    ...(titulo.trim() ? [] : ['Título']),
    ...(formato === 'arquivo' && !arquivo ? ['Arquivo'] : []),
    ...(formato === 'texto' && !conteudo.trim() ? ['Conteúdo'] : []),
  ]

  function escolher(arquivos: File[]) {
    const [primeiro] = arquivos
    if (!primeiro) return
    if (!tipoDoArquivo(primeiro)) {
      setRecusado(`${primeiro.name}: só PDF ou DOCX.`)
      return
    }
    setRecusado(null)
    setArquivo(primeiro)
    if (!titulo.trim()) setTitulo(primeiro.name)
  }

  function enviar() {
    setTentou(true)
    if (faltando.length > 0) return
    const comum = { especialista, titulo: titulo.trim() }
    criar.mutate(
      formato === 'texto'
        ? { ...comum, formato: 'texto', conteudo: conteudo.trim() }
        : { ...comum, formato: tipoDoArquivo(arquivo!)!, arquivo: arquivo! },
      { onSuccess: aoFechar },
    )
  }

  return (
    <form
      aria-label="Novo material"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        enviar()
      }}
    >
      <h3 className="type-h4 text-text-primary">Novo material</h3>

      <div className="flex gap-2" role="group" aria-label="Formato do material">
        <Botao
          variante={formato === 'arquivo' ? 'accent' : 'secundaria'}
          aria-pressed={formato === 'arquivo'}
          onClick={() => setFormato('arquivo')}
        >
          arquivo
        </Botao>
        <Botao
          variante={formato === 'texto' ? 'accent' : 'secundaria'}
          aria-pressed={formato === 'texto'}
          onClick={() => setFormato('texto')}
        >
          texto
        </Botao>
      </div>

      {formato === 'arquivo' ? (
        <div className="flex flex-col gap-2">
          <ZonaDeArquivo
            titulo="arraste o arquivo ou selecione"
            descricao="PDF ou DOCX"
            icone="upload-simple"
            rotulo="Arquivo do material"
            aoEscolher={escolher}
          />
          {arquivo && (
            <span className="rounded-control bg-surface-card text-text-body inline-flex items-center gap-2 self-start px-3 py-1.5 type-sm">
              <i className="ph ph-file-text" aria-hidden="true" />
              {arquivo.name} · {formatarTamanhoDeArquivo(arquivo.size)}
              <button type="button" aria-label={`remover ${arquivo.name}`} onClick={() => setArquivo(null)}>
                <i className="ph ph-x" aria-hidden="true" />
              </button>
            </span>
          )}
          {recusado && <p className="type-sm text-danger-700">{recusado}</p>}
        </div>
      ) : (
        <Campo rotulo="Conteúdo" obrigatorio>
          <AreaDeTexto
            rows={5}
            placeholder="Cole aqui o texto que o especialista deve considerar."
            value={conteudo}
            onChange={(e) => setConteudo(e.target.value)}
          />
        </Campo>
      )}

      <Campo rotulo="Título" obrigatorio>
        <Entrada
          placeholder="ex.: Guia interno de rightsizing EC2"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
        />
      </Campo>

      {tentou && faltando.length > 0 && (
        <p role="alert" className="type-sm text-danger-700">
          Preencha: {faltando.join(', ')}.
        </p>
      )}
      {criar.isError && (
        <p role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(criar.error)}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Botao variante="primaria" type="submit" disabled={criar.isPending}>
          {criar.isPending ? 'enviando…' : 'salvar material'}
        </Botao>
        <Botao variante="discreta" onClick={aoFechar}>
          cancelar
        </Botao>
      </div>
    </form>
  )
}
